/**
 * Pictures for "Stay or go?", drawn in code (no chart library), used by okjapa's preview and Voucha's result page.
 * Colours come from the --vs-* variables when set, as in standing.js.
 */
const INK = 'var(--vs-ink,#1b2b28)';
const MUTED = 'var(--vs-muted,#6b7a76)';
const LINE = 'var(--vs-line,#d6dfdc)';
export const GO = 'var(--vs-go,#f37f20)';
export const STAY = 'var(--vs-stay,#2f8f9d)';
const esc = s => String(s).replace(/[&<>"]/g, c => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;'
})[c]);
export const SCENARIO_LABELS = {
  stronger: 'Stronger',
  same: 'About the same',
  weaker: 'Weaker'
};

/** "C$1.2m", "€85k", "£900": short money for chart labels */
export function shortMoney(v, currency) {
  const sign = v < 0 ? '−' : '';
  const a = Math.abs(v);
  const sym = {
    NGN: '₦',
    USD: '$',
    CAD: 'CA$',
    GBP: '£',
    EUR: '€',
    AUD: 'A$',
    NZD: 'NZ$'
  }[currency] || currency + ' ';
  const n = a >= 1e6 ? (Math.round(a / 1e5) / 10).toString() + 'm' : a >= 1e4 ? Math.round(a / 1e3) + 'k' : a >= 1e3 ? (Math.round(a / 100) / 10).toString() + 'k' : Math.round(a).toString();
  return sign + sym + n;
}

/** rounded steps for the y axis */
function ticks(min, max, n) {
  const span = max - min || 1;
  const raw = span / n;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw);
  const out = [];
  let v = Math.floor(min / step) * step;
  out.push(v);
  while (v < max - step * 0.001) {
    v += step;
    out.push(v);
  }
  return out;
}

/**
 * Net worth by year on both paths. scenarios: [{key, stay[], go[]}]; view: the scenario drawn as the line, the
 * others shown as a band around it. crossover: the year moving pulls ahead, or null.
 */
export function wealthChart(_ref) {
  let {
    scenarios,
    view,
    currency,
    crossover,
    bands = true
  } = _ref;
  const W = 340,
    H = 230,
    L = 54,
    R = 60,
    T = 14,
    B = 28;
  const main = scenarios.find(s => s.key === view) || scenarios[0];
  const years = main.stay.length - 1;
  const all = scenarios.flatMap(s => bands || s.key === view ? s.stay.concat(s.go) : []);
  const ys = ticks(Math.min(0, ...all), Math.max(...all), 4);
  const lo = ys[0],
    hi = ys[ys.length - 1];
  const X = t => L + (W - L - R) * t / years;
  const Y = v => T + (H - T - B) * (1 - (v - lo) / (hi - lo || 1));
  const path = vals => vals.map((v, t) => (t ? 'L' : 'M') + X(t).toFixed(1) + ' ' + Y(v).toFixed(1)).join('');
  const band = key => {
    const up = main[key].map((_, t) => Math.max(...scenarios.map(s => s[key][t])));
    const dn = main[key].map((_, t) => Math.min(...scenarios.map(s => s[key][t])));
    return path(up) + dn.map((v, t) => 'L' + X(t).toFixed(1) + ' ' + Y(v).toFixed(1)).reverse().join('') + 'Z';
  };
  const end = years;
  const label = shortMoney;
  let svg = `<svg class="VStay-chart" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc('In ' + years + ' years: about ' + label(main.go[end], currency) + ' if you move, ' + label(main.stay[end], currency) + ' if you stay' + (crossover ? '. Moving pulls ahead in year ' + crossover : ''))}">`;
  ys.forEach(v => {
    svg += `<path d="M${L} ${Y(v).toFixed(1)}H${W - R}" stroke="${LINE}" stroke-width="${v === 0 ? 1.5 : 1}"/>`;
    svg += `<text x="${L - 6}" y="${(Y(v) + 3.5).toFixed(1)}" text-anchor="end" font-size="10" fill="${MUTED}">${esc(label(v, currency))}</text>`;
  });
  const step = years > 10 ? 5 : years > 5 ? 2 : 1;
  for (let t = 0; t <= years; t += step) {
    svg += `<text x="${X(t).toFixed(1)}" y="${H - 10}" text-anchor="middle" font-size="10" fill="${MUTED}">${t === 0 ? 'Now' : 'Yr ' + t}</text>`;
  }
  if (bands && scenarios.length > 1) {
    svg += `<path d="${band('stay')}" fill="${STAY}" fill-opacity=".13"/>`;
    svg += `<path d="${band('go')}" fill="${GO}" fill-opacity=".15"/>`;
  }
  svg += `<path d="${path(main.stay)}" fill="none" stroke="${STAY}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>`;
  svg += `<path d="${path(main.go)}" fill="none" stroke="${GO}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>`;
  if (crossover) {
    const cx = X(crossover),
      cy = Y(main.go[crossover]);
    svg += `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="5" fill="#fff" stroke="${GO}" stroke-width="2.5"/>`;
  }
  // end labels, nudged apart
  let gy = Y(main.go[end]),
    sy = Y(main.stay[end]);
  if (Math.abs(gy - sy) < 26) {
    const mid = (gy + sy) / 2;
    const up = main.go[end] >= main.stay[end];
    gy = mid + (up ? -13 : 13);
    sy = mid + (up ? 13 : -13);
  }
  gy = Math.max(T + 10, Math.min(H - B - 14, gy));
  sy = Math.max(T + 10, Math.min(H - B - 14, sy));
  [[gy, 'Move', main.go[end], GO], [sy, 'Stay', main.stay[end], STAY]].forEach(_ref2 => {
    let [y, t, v, c] = _ref2;
    svg += `<text x="${W - R + 6}" y="${(y - 1).toFixed(1)}" font-size="11" font-weight="700" fill="${c}">${t}</text>`;
    svg += `<text x="${W - R + 6}" y="${(y + 11).toFixed(1)}" font-size="10.5" font-weight="700" fill="${INK}">${esc(label(v, currency))}</text>`;
  });
  return svg + '</svg>';
}

/**
 * What you own and owe at the end, on each path: stacked bars. sheets: {stay: {savings, property, abroad, debt}, go: {...}}
 */
export function balanceBars(_ref3) {
  let {
    sheets,
    currency
  } = _ref3;
  const W = 340,
    rowH = 64,
    L = 50,
    R = 12,
    T = 8;
  const parts = [['abroad', 'Savings abroad', GO], ['savings', 'Naira savings', STAY], ['property', 'Property in Nigeria', 'var(--vs-property,#7a8f3a)']];
  const rows = [['go', 'Move'], ['stay', 'Stay']];
  const max = Math.max(1, ...rows.map(_ref4 => {
    let [k] = _ref4;
    return parts.reduce((a, _ref5) => {
      let [p] = _ref5;
      return a + Math.max(0, sheets[k][p]);
    }, 0);
  }), ...rows.map(_ref6 => {
    let [k] = _ref6;
    return sheets[k].debt + Math.max(0, -sheets[k].abroad);
  }));
  const S = v => (W - L - R) * v / max;
  const H = T + rowH * 2 + 50;
  let svg = `<svg class="VStay-bars" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(rows.map(_ref7 => {
    let [k, t] = _ref7;
    return t + ': ' + parts.map(_ref8 => {
      let [p, n] = _ref8;
      return n + ' ' + shortMoney(sheets[k][p], currency);
    }).join(', ') + ', owed ' + shortMoney(sheets[k].debt, currency);
  }).join('. '))}">`;
  rows.forEach((_ref9, i) => {
    let [k, t] = _ref9;
    const y = T + i * rowH;
    svg += `<text x="0" y="${y + 17}" font-size="12" font-weight="700" fill="${INK}">${t}</text>`;
    let x = L;
    parts.forEach(_ref0 => {
      let [p,, c] = _ref0;
      const v = Math.max(0, sheets[k][p]);
      if (v <= 0) return;
      svg += `<rect x="${x.toFixed(1)}" y="${y + 4}" width="${Math.max(1.5, S(v)).toFixed(1)}" height="20" fill="${c}"/>`;
      x += S(v);
    });
    // owed: debts, plus an overdrawn balance abroad (the move paid for on borrowed money)
    const owed = sheets[k].debt + Math.max(0, -sheets[k].abroad);
    if (owed > 0) svg += `<rect x="${L}" y="${y + 28}" width="${Math.max(1.5, S(owed)).toFixed(1)}" height="10" fill="var(--vs-lowest,#e06b4f)"/>`;
    svg += `<text x="${L}" y="${y + 52}" font-size="10.5" fill="${MUTED}">Own ${esc(shortMoney(parts.reduce((a, _ref1) => {
      let [p] = _ref1;
      return a + Math.max(0, sheets[k][p]);
    }, 0), currency))}${owed > 0 ? ' · owe ' + esc(shortMoney(owed, currency)) : ''}</text>`;
  });
  // key
  parts.concat([['debt', 'Owed', 'var(--vs-lowest,#e06b4f)']]).forEach((_ref10, i) => {
    let [, n, c] = _ref10;
    const kx = i % 2 * 170,
      ky = H - 32 + Math.floor(i / 2) * 16;
    svg += `<rect x="${kx}" y="${ky}" width="10" height="10" rx="2" fill="${c}"/><text x="${kx + 14}" y="${ky + 9}" font-size="10.5" fill="${MUTED}">${esc(n)}</text>`;
  });
  return svg + '</svg>';
}
flarum.reg.add('voucha-assessments', 'common/stayorgo', { GO: GO,STAY: STAY,SCENARIO_LABELS: SCENARIO_LABELS,shortMoney: shortMoney,wealthChart: wealthChart,balanceBars: balanceBars, });